import React, { useState, useEffect, useCallback } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Paper, Button, Checkbox, ListItemText } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import PricingChart from './PricingChart';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import { apiPost } from '../services/apiClient';
import { useStockSeries } from '../hooks/useStockSeries';
import { useStockPremium } from '../hooks/useStockPremium';
import { periodForMonths, benchmarkLabel } from '../utils/stockOverlay';

// Helper function to fill gaps in timeseries data by carrying forward last good values
const fillTimeseriesGaps = (timeseries) => {
    if (!timeseries || timeseries.length <= 1) {
        return timeseries;
    }

    // Sort by timestamp first
    const sorted = [...timeseries].sort((a, b) =>
        new Date(a.bucket_start) - new Date(b.bucket_start)
    );

    // Helper to normalize date to YYYY-MM-DD string (handles timezone issues)
    const toDateKey = (dateInput) => {
        const d = new Date(dateInput);
        // Use UTC methods to avoid timezone shifts
        const year = d.getUTCFullYear();
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Build a map of existing data points by date
    const dataByDate = new Map();
    for (const point of sorted) {
        const key = toDateKey(point.bucket_start);
        // If multiple points for same day, keep the one with more posts
        const existing = dataByDate.get(key);
        if (!existing || (point.total_posts || 0) > (existing.total_posts || 0)) {
            dataByDate.set(key, point);
        }
    }

    // Get date range
    const startDate = new Date(sorted[0].bucket_start);
    const endDate = new Date(sorted[sorted.length - 1].bucket_start);

    const filled = [];
    let lastGoodData = null;

    // Iterate through each day in the range
    for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
        const dateKey = toDateKey(d);
        const existingData = dataByDate.get(dateKey);

        if (existingData) {
            // We have data for this date
            if ((existingData.total_posts || 0) > 0) {
                lastGoodData = existingData;
                filled.push(existingData);
            } else if (lastGoodData) {
                // Data exists but has zero posts - carry forward sentiment
                filled.push({
                    ...lastGoodData,
                    bucket_start: existingData.bucket_start,
                    total_posts: 0
                });
            } else {
                // No good data yet, push as-is
                filled.push(existingData);
            }
        } else if (lastGoodData) {
            // Missing day - fill with last good data
            filled.push({
                ...lastGoodData,
                bucket_start: `${dateKey}T00:00:00.000Z`,
                total_posts: 0
            });
        }
        // If no lastGoodData and no existing data, skip this day
    }

    // Safety check: if filling produced fewer points than original, return original
    if (filled.length < sorted.length) {
        console.warn('Gap filling produced fewer points than original, returning original data');
        return sorted;
    }

    return filled;
};

// Function to aggregate sentiment data from multiple topics
const aggregateTopicSentimentData = (topicDataArray) => {
    if (!topicDataArray || topicDataArray.length === 0) {
        return null;
    }

    // Use the first topic's structure as template
    const template = topicDataArray[0];
    if (!template || !template.timeseries) {
        return null;
    }

    // Aggregate by bucket_start timestamp
    const aggregatedTimeseries = {};
    let totalPosts = 0;

    topicDataArray.forEach(topicData => {
        if (!topicData || !topicData.timeseries) return;

        topicData.timeseries.forEach(point => {
            const timestamp = point.bucket_start;

            if (!aggregatedTimeseries[timestamp]) {
                aggregatedTimeseries[timestamp] = {
                    bucket_start: timestamp,
                    total_posts: 0,
                    shares: {
                        very_negative: 0,
                        negative: 0,
                        neutral: 0,
                        positive: 0,
                        very_positive: 0
                    },
                    rolling_shares: {
                        very_negative: 0,
                        negative: 0,
                        neutral: 0,
                        positive: 0,
                        very_positive: 0
                    },
                    volume_ratio: 0
                };
            }

            // Aggregate total posts
            aggregatedTimeseries[timestamp].total_posts += (point.total_posts || 0);

            // Aggregate shares (weighted by total_posts)
            const weight = point.total_posts || 0;
            if (weight > 0) {
                Object.keys(point.shares || {}).forEach(sentiment => {
                    aggregatedTimeseries[timestamp].shares[sentiment] += (point.shares[sentiment] || 0) * weight;
                });

                Object.keys(point.rolling_shares || {}).forEach(sentiment => {
                    aggregatedTimeseries[timestamp].rolling_shares[sentiment] += (point.rolling_shares[sentiment] || 0) * weight;
                });

                aggregatedTimeseries[timestamp].volume_ratio += (point.volume_ratio || 0) * weight;
            }
        });

        totalPosts += (topicData.summary?.total_posts || 0);
    });

    // Normalize the weighted sums
    Object.values(aggregatedTimeseries).forEach(point => {
        if (point.total_posts > 0) {
            Object.keys(point.shares).forEach(sentiment => {
                point.shares[sentiment] /= point.total_posts;
            });

            Object.keys(point.rolling_shares).forEach(sentiment => {
                point.rolling_shares[sentiment] /= point.total_posts;
            });

            point.volume_ratio /= point.total_posts;
        }
    });

    // Convert back to array and sort by timestamp
    let sortedTimeseries = Object.values(aggregatedTimeseries).sort((a, b) =>
        new Date(a.bucket_start) - new Date(b.bucket_start)
    );

    // Fill gaps: if missing day or everything is 0, use previous non-zero data
    if (sortedTimeseries.length > 1) {
        const filled = [];
        const start = new Date(sortedTimeseries[0].bucket_start);
        const end = new Date(sortedTimeseries[sortedTimeseries.length - 1].bucket_start);

        let dataIndex = 0;
        let lastGoodData = null;

        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = d.toISOString().split('T')[0];

            // Check if we have data for this date
            if (dataIndex < sortedTimeseries.length &&
                sortedTimeseries[dataIndex].bucket_start.startsWith(dateStr)) {

                const currentData = sortedTimeseries[dataIndex];

                // Check if data has actual content (non-zero posts)
                if (currentData.total_posts > 0) {
                    lastGoodData = currentData;
                    filled.push(currentData);
                } else if (lastGoodData) {
                    // Current data is all zeros, use last good data
                    filled.push({
                        ...lastGoodData,
                        bucket_start: currentData.bucket_start
                    });
                } else {
                    filled.push(currentData);
                }
                dataIndex++;
            } else if (lastGoodData) {
                // Missing day, use last good data
                filled.push({
                    ...lastGoodData,
                    bucket_start: `${dateStr}T00:00:00.000Z`
                });
            }
        }

        sortedTimeseries = filled;
    }

    return {
        timeseries: sortedTimeseries,
        summary: {
            total_posts: totalPosts,
            // Note: other summary fields would need more complex aggregation
        },
        params: template.params // Use template params
    };
};

const SENTIMENT_CONFIGS = {
    pricing: {
        title: "Pricing Sentiment Analysis",
        buckets: {
            veryNegative: { threshold: -6, label: "Very Negative" },
            moderateNegative: { threshold: -3, label: "Moderately Negative" },
            neutral: { threshold: 3, label: "Neutral" },
            moderatePositive: { threshold: 6, label: "Moderately Positive" },
            veryPositive: { threshold: Infinity, label: "Very Positive" }
        }
    },
    regular: {
        title: "Sentiment Analysis",
        buckets: {
            veryNegative: { threshold: 1, label: "Very Poor" },
            moderateNegative: { threshold: 3, label: "Poor" },
            neutral: { threshold: 6, label: "Average" },
            moderatePositive: { threshold: 8, label: "Good" },
            veryPositive: { threshold: Infinity, label: "Excellent" }
        }
    }
};

const SentimentAnalysisChart = ({
    topicId,
    type,
    customTitle = null, // Optional custom title override
    coverageData = null, // Added coverage data prop
    productName = null, // Product name for dynamic title
    allTopicIds = null, // Array of all topic IDs when topicId is "all"
    // New props for externally controlled filters
    timeRange: externalTimeRange = null,
    appliedSources: externalAppliedSources = null,
    hideHeader = false,
    hideDetails = false, // When true, render only chart + metric boxes (no snapshot card / posts list) — used for report screenshots
    onKpiUpdate = null, // Callback to report KPI data to parent
    endDate: externalEndDate = null, // Optional end date cutoff (YYYY-MM-DD string)
    companyTicker = null, // Stock ticker for publicly-traded companies (overlay)
    showStock = false, // Legacy: overlay the stock price line (= overlayMode 'price')
    overlayMode = null, // 'off' | 'price' | 'peers' | 'snp500' | 'sector' (overrides showStock)
    competitors = null, // Array of {id, name} competitor topics to compare (score lines)
    mainSeriesName = null // Legend label for the main score line when comparing
}) => {
    const [chartData, setChartData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Competitor score series: [{id, name, points: [{timestamp, score}]}]
    const [competitorSeries, setCompetitorSeries] = useState([]);

    const [summaryMetrics, setSummaryMetrics] = useState({
        totalPostsEvaluated: 0,
        totalMentionPercent: 0,
        absoluteLowSatisfaction: null,
        absoluteHighSatisfaction: null
    });

    // Internal state for filters (used when not controlled externally)
    const [internalTimeRange, setInternalTimeRange] = useState(12);
    const [selectedSources, setSelectedSources] = useState(['all']);
    const [internalAppliedSources, setInternalAppliedSources] = useState(['all']);
    const [availableSourceTypes] = useState([
        { value: 'all', label: 'All Sources' },
        { value: 'reddit', label: 'Reddit' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'google', label: 'Google Maps' },
        { value: 'yelp', label: 'Yelp' },
        { value: 'apple_app_store', label: 'Apple App Store' },
        { value: 'google_app_store', label: 'Google App Store' }
    ]);

    // Use external values if provided, otherwise use internal state
    const timeRange = externalTimeRange !== null ? externalTimeRange : internalTimeRange;
    const appliedSources = externalAppliedSources !== null ? externalAppliedSources : internalAppliedSources;
    const setTimeRange = externalTimeRange !== null ? () => {} : setInternalTimeRange;
    const setAppliedSources = externalAppliedSources !== null ? () => {} : setInternalAppliedSources;

    // Stock overlay (publicly-traded companies only). 'price' overlays the
    // absolute close; 'peers'/'snp500'/'sector' overlay the relative-performance
    // ("premium") line. overlayMode wins; otherwise fall back to the legacy
    // showStock boolean. (timeRange is in months; 99 = all.)
    const effOverlay = overlayMode || (showStock ? 'price' : 'off');
    const priceMode = effOverlay === 'price';
    const premiumMode = ['peers', 'snp500', 'sector'].includes(effOverlay) ? effOverlay : null;
    const premiumPeriod = timeRange === 99 ? 'all' : periodForMonths(timeRange);

    const { series: stockSeries } = useStockSeries(priceMode ? companyTicker : null);
    const { payload: premium } = useStockPremium(companyTicker, premiumMode, premiumPeriod);
    const showStockOverlay = Boolean(companyTicker) && priceMode;
    const showPremiumOverlay = Boolean(companyTicker) && Boolean(premiumMode) && Boolean(premium?.data?.length);
    const benchLabel = premium ? benchmarkLabel(premium) : '';

    // Posts display state
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [postsLoading, setPostsLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // Data range for display
    const [dataRange, setDataRange] = useState({ start: null, end: null });

    const config = type === 'pricing' ? SENTIMENT_CONFIGS.pricing : SENTIMENT_CONFIGS.regular;

    // Dynamic metric label based on type
    const metricLabel = type
        ? type.split('_').map(word =>
            word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ')
        : 'Quality';

    // Get color based on score intensity (0-100 scale, 50 is neutral)
    const getScoreColor = (score) => {
        if (score >= 50) {
            // Green scale: 50=light green, 100=dark green
            const intensity = Math.min((score - 50) / 40, 1); // 50-90 range for full intensity
            const r = Math.round(134 - (134 - 21) * intensity);
            const g = Math.round(239 - (239 - 128) * intensity);
            const b = Math.round(172 - (172 - 61) * intensity);
            return `rgb(${r}, ${g}, ${b})`;
        } else {
            // Red scale: 50=light red, 0=dark red
            const intensity = Math.min((50 - score) / 40, 1); // 50-10 range for full intensity
            const r = Math.round(252 - (252 - 185) * intensity);
            const g = Math.round(165 - (165 - 28) * intensity);
            const b = Math.round(165 - (165 - 28) * intensity);
            return `rgb(${r}, ${g}, ${b})`;
        }
    };

    const getScoreBg = (score) => {
        if (score >= 50) {
            // Green gradient: 50=very light, 100=medium green
            const intensity = Math.min((score - 50) / 40, 1);
            const startL = 97 - intensity * 15;
            const endL = 93 - intensity * 20;
            return `linear-gradient(135deg, hsl(142, 76%, ${startL}%) 0%, hsl(142, 76%, ${endL}%) 100%)`;
        } else {
            // Red gradient: 50=very light, 0=medium red
            const intensity = Math.min((50 - score) / 40, 1);
            const startL = 97 - intensity * 15;
            const endL = 93 - intensity * 20;
            return `linear-gradient(135deg, hsl(0, 86%, ${startL}%) 0%, hsl(0, 86%, ${endL}%) 100%)`;
        }
    };

    // Handle chart click to fetch posts for the clicked period
    const handleChartClick = useCallback(async ({ timestamp, data }) => {
        if (!topicId || topicId === 'all') return;

        const endDate = new Date(timestamp);
        const startDate = new Date(timestamp);
        startDate.setMonth(startDate.getMonth() - 1);

        const periodLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

        setPostsLoading(true);
        setSelectedPeriod(periodLabel);

        try {
            // Prepare source type parameter
            const sourceTypeParam = appliedSources.includes('all') ? null : appliedSources;

            const response = await apiPost('topic/sentiment/posts', {
                topic_id: topicId,
                start_date: startDate.toISOString(),
                end_date: endDate.toISOString(),
                type: type,
                source_types: sourceTypeParam,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch posts: ${response.status}`);
            }

            const posts = await response.json();
            setPostsReferenced(posts);
        } catch (err) {
            console.error('Error fetching posts:', err);
            setPostsReferenced([]);
        } finally {
            setPostsLoading(false);
        }
    }, [topicId, type, appliedSources]);

    // Close posts display
    const handleClosePosts = useCallback(() => {
        setPostsReferenced([]);
        setSelectedPeriod(null);
    }, []);

    // Clear posts when topic, type, or source changes
    useEffect(() => {
        setPostsReferenced([]);
        setSelectedPeriod(null);
    }, [topicId, type, appliedSources]);

    // Reset source filters to "All Sources" when topic changes
    useEffect(() => {
        setSelectedSources(['all']);
        setInternalAppliedSources(['all']);
    }, [topicId]);

    // Build dynamic title with product name
    const buildTitle = () => {
        if (customTitle) return customTitle;
        if (productName && type === 'pricing') {
            return `${productName} - Pricing`;
        }
        return config.title;
    };

    const title = buildTitle();

    // Stable key so a parent re-render with a new array identity doesn't refetch
    const competitorsKey = (competitors || []).map(c => c.id).join(',');

    useEffect(() => {
        processData();
    }, [timeRange, topicId, type, appliedSources, externalEndDate, competitorsKey]); // Reprocess when these change

    // Handle multi-select source changes in the UI (doesn't trigger fetch until "Go" is clicked)
    const handleSourceChange = (event) => {
        let value = event.target.value;

        // MUI may give a string if autofill is used
        if (typeof value === 'string') {
            value = value.split(',');
        }

        // If "all" is selected together with others, drop "all" and keep specifics
        if (value.includes('all') && value.length > 1) {
            value = value.filter(v => v !== 'all');
        } else if (value.length === 0) {
            // Never allow empty selection – default back to "all"
            value = ['all'];
        }

        setSelectedSources(value);
    };

    const applySourceFilter = () => {
        setAppliedSources(selectedSources);
    };

    const processData = async () => {
        setIsLoading(true);
        setError(null); // Clear any previous errors
        try {
            // Calculate date range - use end of current day to include all data for today
            let endDate;
            if (externalEndDate) {
                endDate = new Date(externalEndDate + 'T23:59:59.999');
            } else {
                endDate = new Date();
                endDate.setHours(23, 59, 59, 999); // Set to end of day
            }
            const displayStartDate = new Date(endDate);

            if (timeRange === 99) {
                // Outback: 3/1/24 to 3/31/25
                displayStartDate.setTime(new Date('2024-03-01').getTime());
                endDate = new Date('2025-03-31');
                endDate.setHours(23, 59, 59, 999);
            } else if (timeRange === 1) {
                // 4 weeks = 28 days
                displayStartDate.setDate(endDate.getDate() - 28);
            } else {
                displayStartDate.setMonth(endDate.getMonth() - timeRange);
            }
            
            // For proper rolling average, we need to fetch data from before our display start
            const dataStartDate = new Date(displayStartDate);
            if (timeRange === 99) {
                // For Outback, fetch 365 days before start for rolling average
                dataStartDate.setFullYear(dataStartDate.getFullYear() - 1);
            } else if (timeRange === 1) {
                // For 4-week view with 30-day rolling, fetch 30 days before start
                dataStartDate.setDate(dataStartDate.getDate() - 30);
            } else {
                // For longer views with 365-day rolling, fetch 365 days before start
                dataStartDate.setFullYear(dataStartDate.getFullYear() - 1);
            }

            // Use the earlier date to ensure we have enough data for the rolling average
            const startDate = dataStartDate;

            // Always use 365-day rolling average, but adjust display frequency based on time range
            let frequency = 'day';
            let rollingLen = 365; // Always 365-day rolling average

            // Adjust frequency based on time range to control how many data points are shown
            if (timeRange === 99) {
                // Outback: daily with 365-day rolling average
                frequency = 'day';
                rollingLen = 365;
            } else if (timeRange === 1) {
                // For 4 weeks view, show daily data with 30-day rolling average
                frequency = 'day';
                rollingLen = 30; // 30-day rolling average for shorter view
            } else if (timeRange <= 3) {
                frequency = 'day';
                rollingLen = 365; // 365 days
            } else if (timeRange <= 12) {
                frequency = 'day';
                rollingLen = 365; // Still 365 days
            } else if (timeRange <= 36) { // Extended weekly range to 36 months
                frequency = 'day';
                rollingLen = 52; // 52 weeks = ~365 days
            } else {
                frequency = 'month';
                rollingLen = 12; // 12 months = ~365 days
            }

            console.log('Calling get_topic_sentiment_overview with params:', {
                topicId,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                rollingLen,
                frequency,
                sources: appliedSources
            });

            let data, error;

            // Prepare source type parameter for the RPC (expects an array).
            // When "all" is included, pass null so the backend can include all sources.
            const sourceTypeParam = appliedSources.includes('all') ? null : appliedSources;

            // Source-mix ("site sample") bias: /topic/sentiment/overview blends
            // sources weighted by post counts, so the score moves whenever the mix
            // moves. Starbucks Customer went from 3% to 95% Google Maps reviews
            // over twelve months as a scrape landed; the raw blend fell 13.5
            // points, standardized it fell 2.5. Most of that "decline" was the
            // arrival of a source, not a change in sentiment.
            //
            // So standardize whenever we draw across ALL sources. With a single
            // source selected the ordinary route is already like-for-like, so
            // leave it alone. topicId 'all' is a client-side aggregate of many
            // topics, not a topic the route can score.
            //
            // Every topic is standardized SOLO — scored on its own window-average
            // source mix — never pooled with the comparison set. Pooled weights
            // keep only the sources every topic shares, which threw away most of
            // the data (Starbucks vs Dutch Bros collapsed to Reddit alone) and
            // re-based the main line the moment the competitor toggle flipped
            // (51 alone, 30 in comparison). A topic's score must not depend on
            // who it is compared against.
            const standardizeAcrossSources = !sourceTypeParam && topicId !== 'all';
            const competitorList = competitors || [];

            // One topic's solo-standardized series, or null so the caller can
            // fall back to the raw blend. Never fails the chart.
            const fetchStandardizedSolo = async (id) => {
                try {
                    const response = await apiPost('topic/sentiment/overview/standardized', {
                        topic_ids: [id],
                        start_date: startDate.toISOString(),
                        end_date: endDate.toISOString(),
                        type,
                        frequency,
                    });
                    if (!response.ok) throw new Error(`Backend error: ${response.status}`);
                    const json = await response.json();
                    if (!json?.data?.[id]?.timeseries?.length) {
                        throw new Error('no standardized series');
                    }

                    console.log(`Standardized sentiment weights for ${id}:`, json.weights);
                    if (json.excludedSources && Object.keys(json.excludedSources).length) {
                        console.log(`Sources excluded from standardization for ${id}:`, json.excludedSources);
                    }
                    if (json.truncatedTo) {
                        console.log(`Standardized series for ${id} starts at`, json.truncatedTo,
                            '- earlier buckets have no data for at least one weighted source');
                    }
                    return json.data[id];
                } catch (stdError) {
                    console.error(`Standardized sentiment failed for ${id}, falling back to raw blend:`, stdError);
                    return null;
                }
            };

            const fetchRawOverview = async (id) => {
                const response = await apiPost('topic/sentiment/overview', {
                    topic_id: id,
                    start_date: startDate.toISOString(),
                    end_date: endDate.toISOString(),
                    type,
                    frequency,
                    source_types: sourceTypeParam || null,
                });
                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }
                const json = await response.json();
                return json.data ?? json ?? null;
            };

            // Competitor comparison: fetch each competitor's series (same window,
            // type, frequency, and source filter) in parallel with the main
            // fetch, on the same solo-standardized-else-raw basis as the main
            // line, so a competitor shows the same number here as on its own
            // page. A failing competitor (no data, restricted access) is skipped
            // rather than failing the chart.
            const competitorPromise = Promise.all(competitorList.map(async (comp) => {
                if (standardizeAcrossSources) {
                    const std = await fetchStandardizedSolo(comp.id);
                    if (std) return { comp, data: std };
                }
                try {
                    return { comp, data: await fetchRawOverview(comp.id) };
                } catch (compError) {
                    console.error(`Error fetching sentiment data for competitor ${comp.id}:`, compError);
                    return { comp, data: null };
                }
            }));

            if (topicId === 'all' && allTopicIds && allTopicIds.length > 0) {
                // Fetch data for all topics and aggregate
                console.log('Fetching sentiment data for all topics:', allTopicIds);

                try {
                    const allTopicData = await Promise.all(
                        allTopicIds.map(async (id) => {
                            console.log(`Fetching sentiment data for topic ${id}`);
                            try {
                                const response = await apiPost('topic/sentiment/overview', {
                                    topic_id: id,
                                    start_date: startDate.toISOString(),
                                    end_date: endDate.toISOString(),
                                    type,
                                    frequency,
                                    source_types: sourceTypeParam || null,
                                });

                                if (!response.ok) {
                                    throw new Error(`Backend error: ${response.status}`);
                                }

                                const json = await response.json();
                                return json.data ?? json ?? null;
                            } catch (topicError) {
                                console.error(`Error fetching sentiment data for topic ${id}:`, topicError);
                                return null;
                            }
                        })
                    );

                    // Filter out failed requests
                    const validTopicData = allTopicData.filter(data => data !== null);

                    if (validTopicData.length === 0) {
                        throw new Error('No valid data received from any topic');
                    }

                    // Aggregate the data
                    console.log('Aggregating sentiment data from', validTopicData.length, 'topics');
                    data = aggregateTopicSentimentData(validTopicData);
                    error = null;

                } catch (aggregationError) {
                    console.error('Error aggregating sentiment data:', aggregationError);
                    error = aggregationError;
                    data = null;
                }
            } else {
                // Single topic: solo-standardized when drawing all sources,
                // raw blend otherwise or when standardization fails.
                data = standardizeAcrossSources ? await fetchStandardizedSolo(topicId) : null;
                if (data) {
                    error = null;
                } else {
                    console.log('Fetching sentiment data for single topic:', topicId);
                    try {
                        data = await fetchRawOverview(topicId);
                        error = null;
                    } catch (fetchError) {
                        error = fetchError;
                        data = null;
                    }
                }
            }

            if (error) {
                console.error('Error fetching sentiment data:', error);
                setError(error.message);
                return;
            }

            if (!data || !data.timeseries || data.timeseries.length === 0) {
                console.log('No timeseries data received');
                setChartData([]);
                return;
            }

            // Fill gaps in timeseries data by carrying forward last good values
            // This handles scenarios where there's no data for extended periods (e.g., 5 months)
            const originalLength = data.timeseries.length;
            data.timeseries = fillTimeseriesGaps(data.timeseries);
            console.log(`Gap filling: ${originalLength} -> ${data.timeseries.length} data points`);

            console.log('Received sentiment data:', {
                params: data.params,
                summary: data.summary,
                timeseriesLength: data.timeseries.length
            });
            
            // Debug volume_ratio values
            console.log('Volume ratio values:', data.timeseries.slice(0, 10).map(point => ({
                date: point.bucket_start,
                volume_ratio: point.volume_ratio,
                total_posts: point.total_posts
            })));

            // Debug: Log raw volume ratios to see what we're working with
            const totalPostsInPeriod = data.timeseries.reduce((sum, point) => sum + (point.total_posts || 0), 0);
            const numBuckets = data.timeseries.length;
            const avgPostsPerBucket = totalPostsInPeriod / numBuckets;
            
            console.log('Volume ratio analysis:', {
                totalPostsInPeriod,
                numBuckets,
                avgPostsPerBucket,
                maxPostsInBucket: Math.max(...data.timeseries.map(p => p.total_posts || 0)),
                minPostsInBucket: Math.min(...data.timeseries.map(p => p.total_posts || 0))
            });
            
            console.log('Raw volume ratios (last 20):', data.timeseries.slice(-20).map((point, i) => ({
                index: data.timeseries.length - 20 + i,
                date: point.bucket_start,
                volume_ratio: point.volume_ratio,
                total_posts: point.total_posts,
                relativeToAvg: point.total_posts / avgPostsPerBucket * 100 // What it should be relative to average
            })));

            // Apply 31-point moving average to volume_ratio (20 previous + current + 10 future)
            const smoothVolumeRatio = data.timeseries.map((_, index) => {
                const rawRatios = [];
                
                // Collect 20 previous, current, and 10 future points
                for (let i = -5; i <= 2; i++) {
                    const targetIndex = index + i;
                    if (targetIndex >= 0 && targetIndex < data.timeseries.length) {
                        rawRatios.push(data.timeseries[targetIndex].volume_ratio || 0);
                    }
                }
                
                // Calculate average of available points
                const sum = rawRatios.reduce((acc, val) => acc + val, 0);
                const average = rawRatios.length > 0 ? sum / rawRatios.length : 0;
                
                // Debug first few calculations
                if (index < 5) {
                    console.log(`Smoothing index ${index}:`, {
                        rawRatios: rawRatios.slice(0, 5), // First 5 values used
                        pointsUsed: rawRatios.length,
                        average: average,
                        originalValue: data.timeseries[index].volume_ratio
                    });
                }
                
                return average;
            });
            
            // Debug: Compare smoothed vs raw values
            console.log('Smoothed vs Raw (last 20):', data.timeseries.slice(-20).map((point, i) => ({
                index: data.timeseries.length - 20 + i,
                raw: point.volume_ratio,
                smoothed: smoothVolumeRatio[data.timeseries.length - 20 + i],
                difference: Math.abs(point.volume_ratio - smoothVolumeRatio[data.timeseries.length - 20 + i])
            })));

            // Transform timeseries data for the chart
            const allChartData = data.timeseries.map((point, index) => ({
                timestamp: new Date(point.bucket_start).getTime(),
                // Use rolling shares, convert to percentages
                percentVeryNegative: (point.rolling_shares.very_negative || 0) * 100,
                percentModerateNegative: (point.rolling_shares.negative || 0) * 100,
                percentNeutral: (point.rolling_shares.neutral || 0) * 100,
                percentModeratePositive: (point.rolling_shares.positive || 0) * 100,
                percentVeryPositive: (point.rolling_shares.very_positive || 0) * 100,
                totalPosts: point.total_posts || 0,
                // Use the smoothed volume_ratio calculated above
                weeklyVolume: smoothVolumeRatio[index] * 100, // Convert to percentage for display
                npsVolume: 1, // Dummy value since PricingChart expects this field
                // Raw shares for hover details
                rawVeryNegative: (point.shares.very_negative || 0) * 100,
                rawNegative: (point.shares.negative || 0) * 100,
                rawNeutral: (point.shares.neutral || 0) * 100,
                rawPositive: (point.shares.positive || 0) * 100,
                rawVeryPositive: (point.shares.very_positive || 0) * 100
            }));
            
            // Filter to only show data points within the requested display range
            // Start at the first actual data point, end at the last actual data point
            const displayEndTimestamp = endDate.getTime();
            const displayStartTimestamp = displayStartDate.getTime();

            // Find the first data point that has actual data (total_posts > 0)
            const firstDataPoint = allChartData.find(point => point.totalPosts > 0);
            // Find the last data point that has actual data (total_posts > 0)
            const lastDataPoint = [...allChartData].reverse().find(point => point.totalPosts > 0);

            // Use the later of: time window start OR first actual data point
            const effectiveStartTimestamp = firstDataPoint
                ? Math.max(firstDataPoint.timestamp, displayStartTimestamp)
                : displayStartTimestamp;

            // Use the earlier of: time window end OR last actual data point
            const effectiveEndTimestamp = lastDataPoint
                ? Math.min(lastDataPoint.timestamp, displayEndTimestamp)
                : displayEndTimestamp;

            const chartData = allChartData.filter(point =>
                point.timestamp >= effectiveStartTimestamp && point.timestamp <= effectiveEndTimestamp
            );

            console.log('Filtered data for display:', {
                totalDataPoints: allChartData.length,
                displayedDataPoints: chartData.length,
                requestedStart: displayStartDate.toISOString(),
                effectiveStart: new Date(effectiveStartTimestamp).toISOString(),
                requestedEnd: endDate.toISOString(),
                effectiveEnd: new Date(effectiveEndTimestamp).toISOString(),
                firstDisplayPoint: chartData[0] ? new Date(chartData[0].timestamp).toISOString() : 'none',
                lastDisplayPoint: chartData[chartData.length - 1] ? new Date(chartData[chartData.length - 1].timestamp).toISOString() : 'none'
            });

            // Store summary metrics
            setSummaryMetrics({
                totalPostsEvaluated: data.summary.total_posts || 0,
                totalMentionPercent: 100, // All posts are NPS for this topic
                absoluteLowSatisfaction: data.summary.absolute_low_rating ? {
                    value: data.summary.absolute_low_rating,
                    date: data.summary.absolute_low_time
                } : null,
                absoluteHighSatisfaction: data.summary.absolute_high_rating ? {
                    value: data.summary.absolute_high_rating,
                    date: data.summary.absolute_high_time
                } : null
            });

            setChartData(chartData);

            // Build competitor score lines over the same display window, using
            // the same rolling-share NPS formula as the main score line.
            const competitorResults = await competitorPromise;
            const compSeries = competitorResults
                .filter(r => r.data && Array.isArray(r.data.timeseries) && r.data.timeseries.length > 0)
                .map(({ comp, data: compData }) => {
                    const filled = fillTimeseriesGaps(compData.timeseries);
                    const points = filled
                        .map(pt => {
                            const shares = pt.rolling_shares || {};
                            const promoters = (shares.very_positive || 0) * 100 + ((shares.positive || 0) * 100) * 0.5;
                            const detractors = (shares.very_negative || 0) * 100 + (shares.negative || 0) * 100 + ((shares.neutral || 0) * 100) * 0.33;
                            return {
                                timestamp: new Date(pt.bucket_start).getTime(),
                                score: Math.round(((promoters - detractors) + 100) / 2)
                            };
                        })
                        .filter(p => p.timestamp >= effectiveStartTimestamp && p.timestamp <= effectiveEndTimestamp);
                    return { id: comp.id, name: comp.name, points };
                })
                .filter(series => series.points.length > 0);
            setCompetitorSeries(compSeries);

            // Update data range for display
            if (chartData.length > 0) {
                setDataRange({
                    start: new Date(chartData[0].timestamp),
                    end: new Date(chartData[chartData.length - 1].timestamp)
                });
            } else {
                setDataRange({ start: null, end: null });
            }

            // Calculate KPI data for parent component
            if (onKpiUpdate && chartData.length > 0) {
                // Get recent NPS (average of last 4 data points for stability)
                const recentPoints = chartData.slice(-4);
                const currentScore = recentPoints.reduce((sum, p) => sum + (p.nps || 0), 0) / recentPoints.length;

                // Calculate delta vs prior period (compare first half to second half)
                const midPoint = Math.floor(chartData.length / 2);
                const firstHalf = chartData.slice(0, midPoint);
                const secondHalf = chartData.slice(midPoint);

                const firstHalfAvg = firstHalf.length > 0
                    ? firstHalf.reduce((sum, p) => sum + (p.nps || 0), 0) / firstHalf.length
                    : 0;
                const secondHalfAvg = secondHalf.length > 0
                    ? secondHalf.reduce((sum, p) => sum + (p.nps || 0), 0) / secondHalf.length
                    : 0;

                const delta = secondHalfAvg - firstHalfAvg;

                // Exact values shown in the on-slide metric boxes — same
                // formulas as the cards above so the Report Generator can
                // surface identical numbers everywhere else (exec summary,
                // banners, the AI prompt). Don't recompute these elsewhere.
                const boxCalcNps = (p) => {
                    const promoters = (p.percentVeryPositive || 0) + ((p.percentModeratePositive || 0) * 0.5);
                    const detractors = (p.percentVeryNegative || 0) + (p.percentModerateNegative || 0) + ((p.percentNeutral || 0) * 0.33);
                    return ((promoters - detractors) + 100) / 2;
                };
                const boxPts = chartData.map(p => ({ timestamp: p.timestamp, nps: boxCalcNps(p) }));
                const boxPeak = boxPts.reduce((m, p) => (p.nps > m.nps ? p : m), boxPts[0]);
                const boxLow = boxPts.reduce((m, p) => (p.nps < m.nps ? p : m), boxPts[0]);
                const fmtBoxDate = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

                onKpiUpdate({
                    currentScore: Math.round(currentScore),
                    delta: Math.round(delta),
                    totalMentions: data.summary.total_posts || 0,
                    // Slide-box values (the source of truth):
                    currentNps: Math.round(boxPts[boxPts.length - 1].nps),
                    peakNps: Math.round(boxPeak.nps),
                    peakDate: fmtBoxDate(boxPeak.timestamp),
                    lowNps: Math.round(boxLow.nps),
                    lowDate: fmtBoxDate(boxLow.timestamp),
                    postsAnalyzed: data.summary.total_posts || 0,
                });
            }
        } catch (err) {
            console.error('Error processing data:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        const shimmerStyle = {
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s ease-in-out infinite',
            borderRadius: '8px',
        };
        return (
            <div style={{ padding: hideHeader ? '0' : '20px' }}>
                <style>{`
                    @keyframes shimmer {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                `}</style>
                {/* Chart skeleton */}
                <div style={{ ...shimmerStyle, width: '100%', maxWidth: '640px', height: '360px', marginBottom: '20px' }} />
                {/* Insight cards skeleton */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    {[0,1,2,3].map(i => (
                        <div key={i} style={{ ...shimmerStyle, height: '100px' }} />
                    ))}
                </div>
                {/* Snapshot card skeleton */}
                <div style={{ ...shimmerStyle, width: '100%', height: '140px' }} />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                margin: hideHeader ? '0' : '20px',
                padding: '20px 24px',
                backgroundColor: '#fff5f5',
                border: '1px solid #fecaca',
                borderLeft: '4px solid #ef4444',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
            }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: '1px' }}>
                    <path d="M10 2L2 18h16L10 2z" stroke="#ef4444" strokeWidth="1.5" fill="none"/>
                    <line x1="10" y1="8" x2="10" y2="12" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
                    <circle cx="10" cy="15" r="0.8" fill="#ef4444"/>
                </svg>
                <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>
                        Error loading data
                    </div>
                    <div style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '12px' }}>
                        {error}
                    </div>
                    <button
                        onClick={() => processData()}
                        style={{
                            padding: '6px 16px',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#991b1b',
                            backgroundColor: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fecaca'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: hideHeader ? '0' : '20px' }}>
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            {!hideHeader && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <h2 style={{ margin: 0 }}>
                        {productName ? `${productName} - ${title}` : title} {timeRange === 99 ? '(3/1/24 - 3/31/25)' : `(Last ${timeRange} Months)`}
                    </h2>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <FormControl style={{ minWidth: 220 }}>
                            <InputLabel id="source-type-label">Sources</InputLabel>
                            <Select
                                labelId="source-type-label"
                                multiple
                                value={selectedSources}
                                label="Sources"
                                onChange={handleSourceChange}
                                renderValue={(selected) => {
                                    if (selected.includes('all')) {
                                        return 'All Sources';
                                    }
                                    const labels = availableSourceTypes
                                        .filter((source) => selected.includes(source.value))
                                        .map((source) => source.label);
                                    return labels.join(', ');
                                }}
                            >
                                {availableSourceTypes.map((source) => (
                                    <MenuItem key={source.value} value={source.value}>
                                        <Checkbox checked={selectedSources.indexOf(source.value) > -1} />
                                        <ListItemText primary={source.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{ minWidth: 140 }}>
                            <InputLabel>Time Range</InputLabel>
                            <Select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                label="Time Range"
                            >
                                <MenuItem value={1}>4 Weeks</MenuItem>
                                <MenuItem value={3}>3 Months</MenuItem>
                                <MenuItem value={6}>6 Months</MenuItem>
                                <MenuItem value={12}>12 Months</MenuItem>
                                <MenuItem value={14}>14 Months</MenuItem>
                                <MenuItem value={22}>22 Months</MenuItem>
                                <MenuItem value={24}>24 Months</MenuItem>
                                <MenuItem value={30}>30 Months</MenuItem>
                                <MenuItem value={36}>36 Months</MenuItem>
                                <MenuItem value={48}>48 Months</MenuItem>
                                <MenuItem value={60}>60 Months</MenuItem>
                                <MenuItem value={99}>Outback</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={applySourceFilter}
                        >
                            Go
                        </Button>
                    </div>
                </div>
            )}

            {chartData.length === 0 ? (
                <div>No data available for the selected time period</div>
            ) : (
                <>
                {/* Data coverage mini-bar */}
                {dataRange.start && dataRange.end && (() => {
                    const requestedMs = timeRange * 30.44 * 24 * 60 * 60 * 1000;
                    const actualMs = dataRange.end.getTime() - dataRange.start.getTime();
                    const coveragePct = Math.min(Math.round((actualMs / requestedMs) * 100), 100);
                    const barColor = coveragePct >= 80 ? '#22c55e' : coveragePct >= 50 ? '#eab308' : '#ef4444';
                    return (
                        <div style={{ marginBottom: '12px' }}>
                            <div style={{
                                width: '100%',
                                height: '4px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '2px',
                                overflow: 'hidden',
                                marginBottom: '6px',
                            }}>
                                <div style={{
                                    width: `${coveragePct}%`,
                                    height: '100%',
                                    backgroundColor: barColor,
                                    borderRadius: '2px',
                                    transition: 'width 0.4s ease',
                                }} />
                            </div>
                            <div style={{ fontSize: '11px', color: '#888' }}>
                                {dataRange.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                {' — '}
                                {dataRange.end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                <span style={{ color: '#aaa', marginLeft: '8px' }}>{coveragePct}% coverage</span>
                            </div>
                        </div>
                    );
                })()}
                <Paper elevation={2} style={{ padding: '20px', marginBottom: '20px', animation: 'fadeInUp 0.4s ease both' }}>
                    <div style={{ width: '100%', height: '600px' }}>
                        <PricingChart
                            data={chartData}
                            showTwoYears={timeRange >= 22}
                            defaultLinearScale={topicId === 'all'}
                            onPointClick={topicId !== 'all' ? handleChartClick : undefined}
                            stockSeries={showStockOverlay ? stockSeries : null}
                            stockTicker={companyTicker}
                            premiumData={showPremiumOverlay ? premium.data : null}
                            benchLabel={benchLabel}
                            competitorSeries={competitorSeries.length > 0 ? competitorSeries : null}
                            mainLineName={competitorSeries.length > 0 ? (mainSeriesName || 'Sentiment Score') : null}
                        />
                    </div>
                </Paper>
                {/* Note any requested competitors with no data under the current filters */}
                {competitors && competitors.length > 0 && competitorSeries.length < competitors.length && (
                    <div style={{ fontSize: '11px', color: '#999', marginTop: '-12px', marginBottom: '16px' }}>
                        No {metricLabel} data for the selected filters: {competitors
                            .filter(c => !competitorSeries.some(s => s.id === c.id))
                            .map(c => c.name)
                            .join(', ')}
                    </div>
                )}
                </>
            )}

            {/* INSIGHTS Strip - Key Metrics Row */}
            {chartData.length > 0 && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '16px',
                    marginTop: '20px',
                    animation: 'fadeInUp 0.4s ease both 60ms',
                }}>
                    {/* NPS Score - Hero Metric */}
                    {(() => {
                        const calcNPS = (point) => {
                            const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
                            const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
                            const rawNPS = promoters - detractors;
                            return (rawNPS + 100) / 2;
                        };

                        const latest = chartData[chartData.length - 1];
                        const currentNPS = calcNPS(latest);
                        const scoreColor = getScoreColor(currentNPS);

                        let deltaNPS = null;
                        if (chartData.length > 30) {
                            const priorPoint = chartData[chartData.length - 31];
                            const priorNPS = calcNPS(priorPoint);
                            deltaNPS = currentNPS - priorNPS;
                        }

                        return (
                            <div style={{
                                padding: '20px',
                                background: getScoreBg(currentNPS),
                                borderRadius: '12px',
                                borderBottom: `3px solid ${scoreColor}`,
                            }}>
                                <div style={{ fontSize: '32px', fontWeight: 700, color: scoreColor }}>
                                    {Math.round(currentNPS) === 0 ? '0' : currentNPS.toFixed(0)}
                                </div>
                                <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                                    {metricLabel} Score
                                </div>
                                {deltaNPS !== null && (
                                    <div style={{
                                        fontSize: '11px',
                                        color: deltaNPS >= 0 ? '#16a34a' : '#dc2626',
                                        marginTop: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {deltaNPS >= 0 ? (
                                            <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 1L9 6H1L5 1Z" fill="#16a34a"/></svg>
                                        ) : (
                                            <svg width="10" height="10" viewBox="0 0 10 10"><path d="M5 9L1 4H9L5 9Z" fill="#dc2626"/></svg>
                                        )}
                                        <span>{Math.abs(deltaNPS).toFixed(0)} vs 30d ago</span>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Peak NPS */}
                    {(() => {
                        const calcNPS = (point) => {
                            const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
                            const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
                            const rawNPS = promoters - detractors;
                            return (rawNPS + 100) / 2;
                        };
                        const npsData = chartData.map(point => ({
                            timestamp: point.timestamp,
                            nps: calcNPS(point)
                        }));
                        const peak = npsData.reduce((max, point) =>
                            point.nps > max.nps ? point : max
                        , npsData[0]);
                        const scoreColor = getScoreColor(peak.nps);

                        return (
                            <div style={{
                                padding: '20px',
                                background: getScoreBg(peak.nps),
                                borderRadius: '12px',
                                borderBottom: `3px solid ${scoreColor}`,
                            }}>
                                <div style={{ fontSize: '32px', fontWeight: 700, color: scoreColor }}>
                                    {Math.round(peak.nps) === 0 ? '0' : peak.nps.toFixed(0)}
                                </div>
                                <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                                    Peak {metricLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                    {new Date(peak.timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Low NPS */}
                    {(() => {
                        const calcNPS = (point) => {
                            const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
                            const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
                            const rawNPS = promoters - detractors;
                            return (rawNPS + 100) / 2;
                        };
                        const npsData = chartData.map(point => ({
                            timestamp: point.timestamp,
                            nps: calcNPS(point)
                        }));
                        const low = npsData.reduce((min, point) =>
                            point.nps < min.nps ? point : min
                        , npsData[0]);
                        const scoreColor = getScoreColor(low.nps);

                        return (
                            <div style={{
                                padding: '20px',
                                background: getScoreBg(low.nps),
                                borderRadius: '12px',
                                borderBottom: `3px solid ${scoreColor}`,
                            }}>
                                <div style={{ fontSize: '32px', fontWeight: 700, color: scoreColor }}>
                                    {Math.round(low.nps) === 0 ? '0' : low.nps.toFixed(0)}
                                </div>
                                <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                                    Low {metricLabel}
                                </div>
                                <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                                    {new Date(low.timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Total Posts */}
                    <div style={{
                        padding: '20px',
                        background: 'linear-gradient(135deg, #eef1fb 0%, #e2e7f7 100%)',
                        borderRadius: '12px',
                        borderBottom: '3px solid #1042c1',
                    }}>
                        <div style={{ fontSize: '32px', fontWeight: 700, color: '#1042c1' }}>
                            {summaryMetrics.totalPostsEvaluated >= 1000
                                ? `${(summaryMetrics.totalPostsEvaluated / 1000).toFixed(1)}k`
                                : summaryMetrics.totalPostsEvaluated.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '13px', color: '#555', fontWeight: 500, marginTop: '4px' }}>
                            Posts Analyzed
                        </div>
                        <div style={{ fontSize: '11px', color: '#888', marginTop: '6px' }}>
                            {timeRange === 1 ? 'Past 4 Weeks' : timeRange === 99 ? 'Mar 2024 – Mar 2025' : `Past ${timeRange} Months`}
                        </div>
                    </div>
                </div>
            )}

            {/* CURRENT SNAPSHOT Card */}
            {!hideDetails && chartData.length > 0 && (() => {
                const snapshotCalcNPS = (point) => {
                    const promoters = (point.percentVeryPositive || 0) + ((point.percentModeratePositive || 0) * 0.5);
                    const detractors = (point.percentVeryNegative || 0) + (point.percentModerateNegative || 0) + ((point.percentNeutral || 0) * 0.33);
                    const rawNPS = promoters - detractors;
                    return (rawNPS + 100) / 2;
                };
                const snapshotLatest = chartData[chartData.length - 1];
                const healthScore = Math.round(snapshotCalcNPS(snapshotLatest));
                const healthColor = healthScore >= 50 ? '#16a34a' : '#dc2626';
                const healthBg = healthScore >= 50 ? '#f0fdf4' : '#fef2f2';

                return (
                <Paper elevation={2} style={{ padding: '24px', marginTop: '16px', animation: 'fadeInUp 0.4s ease both 120ms' }}>
                    <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
                        {/* Left: Dataset Details */}
                        <div style={{ flex: '0 0 200px' }}>
                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
                                Dataset
                            </div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#333' }}>
                                {summaryMetrics.totalPostsEvaluated.toLocaleString()}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                posts evaluated
                            </div>
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '12px' }}>
                                {new Date(chartData[0].timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                {' — '}
                                {new Date(chartData[chartData.length - 1].timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                            </div>
                        </div>

                        {/* Right: Sentiment Distribution */}
                        <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Current Sentiment Distribution
                                    </div>
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        color: healthColor,
                                        backgroundColor: healthBg,
                                        border: `1px solid ${healthColor}20`,
                                    }}>
                                        {healthScore}
                                    </span>
                                </div>
                                <div style={{ fontSize: '10px', color: '#999' }}>
                                    As of {new Date(chartData[chartData.length - 1].timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </div>
                            </div>
                            {/* Stacked horizontal bar */}
                            <div style={{
                                display: 'flex',
                                height: '40px',
                                borderRadius: '8px',
                                overflow: 'hidden',
                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)'
                            }}>
                                {(() => {
                                    const latest = chartData[chartData.length - 1];
                                    const segments = [
                                        { key: 'veryNegative', color: '#F2857B', label: 'Very Poor' },
                                        { key: 'moderateNegative', color: '#F6BBBA', label: 'Poor' },
                                        { key: 'neutral', color: '#B7B7B7', label: 'Average' },
                                        { key: 'moderatePositive', color: '#BED5BA', label: 'Good' },
                                        { key: 'veryPositive', color: '#98D586', label: 'Excellent' }
                                    ];
                                    return segments.map(seg => {
                                        const value = latest[`percent${seg.key.charAt(0).toUpperCase() + seg.key.slice(1)}`] || 0;
                                        if (value < 0.5) return null;
                                        return (
                                            <div
                                                key={seg.key}
                                                style={{
                                                    width: `${value}%`,
                                                    backgroundColor: seg.color,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                title={`${seg.label}: ${value.toFixed(1)}%`}
                                            >
                                                {value > 10 && (
                                                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#333', textShadow: '0 1px 1px rgba(255,255,255,0.5)' }}>
                                                        {value.toFixed(0)}%
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                            {/* Legend */}
                            <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                                {[
                                    { color: '#F2857B', label: 'Very Poor' },
                                    { color: '#F6BBBA', label: 'Poor' },
                                    { color: '#B7B7B7', label: 'Average' },
                                    { color: '#BED5BA', label: 'Good' },
                                    { color: '#98D586', label: 'Excellent' }
                                ].map(item => (
                                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: item.color }} />
                                        <span style={{ fontSize: '11px', color: '#555' }}>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </Paper>
                );
            })()}

            {/* Posts Display */}
            {!hideDetails && postsLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <CircularProgress />
                </div>
            )}

            {!hideDetails && !postsLoading && postsReferenced.length > 0 && (
                <FilteredPostsDisplay
                    posts={postsReferenced}
                    onClose={handleClosePosts}
                    title={`${metricLabel} Posts`}
                    subtitle={selectedPeriod}
                    ratingDisplayName={`${metricLabel} Rating`}
                    autoScroll={true}
                    ratingColorScheme={{ high: 7, low: 4 }}
                />
            )}
        </div>
    );
};

export default SentimentAnalysisChart; 